import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { useLayout } from "../Context/LayoutContext";
import { useChartData } from "../Context/chartContext";
import { message, Grid, Card } from "antd";
import Axios from "../Axios";
import success from "../Assets/success.mp3";
import lose from "../Assets/lose.mp3";
import consts from "../consts";
import $ from "jquery";
import { Switch } from "antd";
import down from "../Assets/down_arrow.webp";
import up from "../Assets/up_arrow.webp";

const STORAGE_KEY = "activeBets";

const { useBreakpoint } = Grid;

const getActiveBets = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const saveActiveBets = (bets) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bets));
};
// ----------------------------------------------------------------------
// Aggregates a list of 1s klines into 5s candles.
// Returns an array of { time, open, high, low, close }.
// ----------------------------------------------------------------------
function aggregate1sTo5s(klines1s) {
  const candles = [];

  // klines1s is expected to be array of [time, open, high, low, close, ...]
  // Sort just in case
  klines1s.sort((a, b) => a[0] - b[0]);

  let currentCandle = null;

  for (const k of klines1s) {
    const timeMs = k[0];
    const open = parseFloat(k[1]);
    const high = parseFloat(k[2]);
    const low = parseFloat(k[3]);
    const close = parseFloat(k[4]);

    // Canvas.jsx time is usually seconds for lightweight-charts
    const timeSec = Math.floor(timeMs / 1000);
    // Round down to nearest 5s
    const bucketTime = Math.floor(timeSec / 5) * 5;

    if (!currentCandle) {
      currentCandle = {
        time: bucketTime,
        open,
        high,
        low,
        close,
      };
    } else if (currentCandle.time === bucketTime) {
      // Update existing candle
      currentCandle.high = Math.max(currentCandle.high, high);
      currentCandle.low = Math.min(currentCandle.low, low);
      currentCandle.close = close;
    } else {
      // Push finished candle and start new one
      candles.push(currentCandle);
      currentCandle = {
        time: bucketTime,
        open,
        high,
        low,
        close,
      };
    }
  }

  if (currentCandle) {
    candles.push(currentCandle);
  }

  // Post-Process: Inject synthetic noise for "flat" candles to match live look
  // Aggressive: Enforce minimum body and minimum range
  candles.forEach((c) => {
    const price = c.close;
    const randomness = 0.5 + Math.random(); // 0.5x to 1.5x variation

    // Min Range: ~0.025%
    const minRange = price * 0.00025 * randomness;
    // Min Body: ~0.005% (Ensure it's not a flat line)
    const minBody = price * 0.00005 * randomness;

    let range = c.high - c.low;
    let body = Math.abs(c.open - c.close);

    // 1. Expand Range if too small
    if (range < minRange) {
      const diff = minRange - range;
      const half = diff / 2;
      c.high += half;
      c.low -= half;
      // Update range var
      range = c.high - c.low;
    }

    // 2. Expand Body if too small (make it a visible rectangle)
    if (body < minBody) {
      const diff = minBody - body;
      // Push open away from close
      // Direction depends on current candle direction or random if doji
      const isBull = c.close >= c.open;
      // If doji, pick random direction
      const direction =
        c.close === c.open ? (Math.random() > 0.5 ? 1 : -1) : isBull ? 1 : -1;

      if (direction === 1) {
        // Bullish, lower open
        c.open -= diff;
      } else {
        // Bearish, raise open
        c.open += diff;
      }
    }

    // 3. Safety Clamp: Ensure Body is inside Range
    // (Visual glitches differ by library, but usually High >= Open/Close >= Low)
    c.high = Math.max(c.high, c.open, c.close);
    c.low = Math.min(c.low, c.open, c.close);
  });

  return candles;
}

const INTERVALS = {
  "5s": { apiInterval: "1s", isCustom: true, label: "5s" },
  "1m": { apiInterval: "1", isCustom: false, label: "1m" },
  // '3m': { apiInterval: '3m', isCustom: false, label: '3m' },
  "5m": { apiInterval: "5", isCustom: false, label: "5m" },
  "1h": { apiInterval: "60", isCustom: false, label: "1H" },
  // '1d': { apiInterval: '1d', isCustom: false, label: '1D' },
};

const DURATION_OPTIONS = [
  { label: "5s", value: 5 },
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
  { label: "1m", value: 60 },
  { label: "5m", value: 300 },
  { label: "10m", value: 600 },
];

const DURATION_OPTIONS2 = [
  { label: "5s", value: 5 },
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
  { label: "1m", value: 60 },
  { label: "5m", value: 300 },
  { label: "10m", value: 600 },
];

const Flow5sCandles = ({ chartId, tab, isMatchedIndex }) => {
  const { getTradeHistory, percent, getWallet, wallet, escrow, tradeChartRef } =
    useLayout();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { priceRef } = useChartData();

  const screens = useBreakpoint();

  // HighChat State
  const [activeInterval, setActiveInterval] = useState("5s");
  const [quantity, setQuantity] = useState(100);

  // HANDLERS
  const inc = () => setQuantity((q) => q + 1);
  const dec = () => setQuantity((q) => (q > 1 ? q - 1 : 1));
  const [isRunning, setIsRunning] = useState(false);

  // Trade Logic State
  const [trades, setTrades] = useState([]); // Array of trade objects
  const [duration, setDuration] = useState(15); // Default 15s
  const [timerDisplay, setTimerDisplay] = useState("00:00"); // Countdown state
  const [resultPopup, setResultPopup] = useState(null); // { win: boolean, amount: number, currency: string }
  const [isBettingDisabled, setIsBettingDisabled] = useState(false); // Disable buttons during bet + cleanup
  const [isLoading, setIsLoading] = useState(false); // Chart loading state
  const [buyOneClick, setBuyOneClick] = useState(false);

  // Initial Zoom State
  const initialZoomDoneRef = useRef(false);

  const tradesRef = useRef([]); // Ref for access in loops
  tradesRef.current = trades;

  // Map to hold PriceLines for active trades
  const priceLinesRef = useRef(new Map()); // Map<tradeId, IPriceLine>

  // Store active bets in ref for access in loops
  const activeBetsRef = useRef([]);

  const containerRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const chartRef = useRef(null);

  // Pulse Ref
  const pulseRef = useRef(null);

  // Buffer to hold recent 1s klines for the *current* forming 5s candle
  const active1sCandlesRef = useRef([]);

  // Animation Refs
  // We store the "target" price (real latest) and "current" value (displayed)
  const targetPriceRef = useRef(null);
  const animPriceRef = useRef(null);
  const rafRef = useRef(null);

  const socketRef = useRef(null);

  // Real Data Ref to separate from Visual (spikes/min body)
  const currentCandleRealDataRef = useRef({
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    time: 0,
  });
  const lastForexTimestampRef = useRef(0);

  // Noise State for Fake Activity
  const noiseRef = useRef(0);
  const noiseVelocityRef = useRef(0); // For smooth momentum

  // Helpers to update markers
  const spikeStateRef = useRef({ top: 0, bottom: 0 });

  // Auto-Scroll Reset Timer
  const autoScrollTimerRef = useRef(null);

  // Track bets currently being processed to prevent double-firing in the fast tick loop
  const processingBetsRef = useRef(new Set());

  //currency pair
  const currencyPair =
    tab?.typ === "forex"
      ? tab.title.toLowerCase()
      : tab?.title?.replace("/", "").toUpperCase() || "BTCUSDT";

  // Playsound helpers
  const playAlertSound = () => {
    const audio = new Audio(
      "https://actions.google.com/sounds/v1/alarms/beep_short.ogg",
    );
    audio.play().catch(() => { });
  };
  const playSucccessSound = () => {
    const audio = new Audio(success);
    audio.play().catch(() => { });
  };
  const playLossSound = () => {
    const audio = new Audio(lose);
    audio.play().catch(() => { });
  };

  // Helper: Finish a bet
  const finishBet = async (bet) => {
    // Remove from persistent storage
    const remaining = getActiveBets().filter((b) => b.betId !== bet.betId);
    saveActiveBets(remaining);

    const currentPrice = animPriceRef.current; // Use Visual Price (with noise)
    const isWin =
      bet.direction === "high"
        ? currentPrice > bet.start_price
        : currentPrice < bet.start_price;

    const profitAmount = isWin
      ? bet.quantity * bet.payoutPercent
      : -bet.quantity;

    // Visual Result Update
    setTrades((prev) =>
      prev.map((t) => {
        if (t.betId === bet.betId) {
          return {
            ...t,
            status: isWin ? "WON" : "LOST",
            resultPrice: currentPrice,
            resultTime: Date.now(),
          };
        }
        return t;
      }),
    );

    // Trigger Popup
    setResultPopup({
      win: isWin,
      amount: isWin ? profitAmount : bet.quantity, // Show profit if win, stake if loss
      currency: "$",
    });

    // Auto-hide popup
    setTimeout(() => setResultPopup(null), 3000);

    if (isWin) playSucccessSound();
    else playLossSound();

    // API Call
    try {
      await Axios.post(
        "/users/placeTrade",
        {
          ...bet,
          end_price: currentPrice,
          result: isWin,
          type: tab?.typ === "forex" ? "forex" : "crypto",
          url: tab?.imgSrc,
        },
        { headers: { Authorization: localStorage.getItem("nzanzi") } },
      );

      // Update Context
      getWallet();
      getTradeHistory();
    } catch (e) {
      console.error("Trade API Error", e);
    }

    // Cleanup visual after delay
    setTimeout(() => {
      setTrades((prev) => prev.filter((t) => t.betId !== bet.betId));
      setIsBettingDisabled(false); // Re-enable buttons after cleanup
    }, 3000);
  };

  // Helper: Place a bet
  const handleBet = async (direction) => {
    if (isBettingDisabled) return; // Prevent new bets
    if (quantity <= 0) return message.error("Invalid amount");
    // if (!wallet?.balance || quantity > wallet.balance - escrow)
    //   return message.error("Insufficient balance");

    setIsBettingDisabled(true); // Disable immediately

    playAlertSound();

    const startPrice = animPriceRef.current; // Use Visual Price (with noise)
    if (!startPrice) return;

    const startTime = Date.now();
    const endTime = startTime + duration * 1000;
    const betId = `bet-${startTime}`;

    // Get Chart Time (seconds)
    let chartTime = Math.floor(startTime / 1000);
    if (candleSeriesRef.current) {
      const data = candleSeriesRef.current.data();
      if (data.length > 0) chartTime = data[data.length - 1].time;
    }

    const bet = {
      betId,
      chartId: chartId || "default",
      direction, // 'high' or 'low'
      quantity,
      start_price: startPrice,
      startTime,
      endTime,
      duration: duration,
      chartTime,
      status: "OPEN",
      payoutPercent: 0.88, // Default or dynamic
      pair: tab?.title || "BTC/USDT",
    };

    // Save Local
    saveActiveBets([...getActiveBets(), bet]);

    // Update State
    setTrades((prev) => [...prev, bet]);
  };

  // mobiel timer (+, -)
  // Find current index
  const currentIndex = DURATION_OPTIONS2.findIndex(
    (opt) => opt.value === duration,
  );

  // Handlers
  const incre = () => {
    if (currentIndex < DURATION_OPTIONS2.length - 1) {
      setDuration(DURATION_OPTIONS2[currentIndex + 1].value);
    }
  };

  const decre = () => {
    if (currentIndex > 0) {
      setDuration(DURATION_OPTIONS2[currentIndex - 1].value);
    }
  };
  // Restore Bets on Mount
  useEffect(() => {
    const saved = getActiveBets();
    // Filter for this chart? If tab agnostic, maybe load all?
    // HighChat filters by chartId.
    const myBets = saved.filter((b) => b.chartId === (chartId || "default"));

    // Check if any expired while offline
    const now = Date.now();
    const valid = [];
    myBets.forEach((b) => {
      if (now > b.endTime) {
        // Should technically process result but price might be gone.
        // Simple: just mark lost or ignore. HighChat processes it.
        // For now, let's load them as OPEN and let the loop finish them?
        // Or if very old, discard.
        if (now - b.endTime < 5000) valid.push(b);
      } else {
        valid.push(b);
      }
    });
    setTrades(valid);
    activeBetsRef.current = valid;
  }, [chartId]);

  // Trade Loop: Check expirations - MOVED TO TICK FUNCTION
  // useEffect(() => {
  //     const intervalId = setInterval(() => {
  //         const now = Date.now();
  //         let changed = false;
  //
  //         // We iterate over STATE trades
  //         trades.forEach(t => {
  //             if (t.status === 'OPEN' && now >= t.endTime) {
  //                 finishBet(t);
  //             }
  //         });
  //     }, 500);
  //     return () => clearInterval(intervalId);
  // }, [trades]);

  // Update markers when trades change
  useEffect(() => {
    if (candleSeriesRef.current) {
      const series = candleSeriesRef.current;
      const markers = [];

      trades.forEach((t) => {
        // Entry
        markers.push({
          time: t.chartTime,
          position: t.direction === "high" ? "belowBar" : "aboveBar",
          color: t.direction === "high" ? "#22c55e" : "#ef4444",
          shape: t.direction === "high" ? "arrowUp" : "arrowDown",
          text: `$${t.quantity}`, // Clean entry text
        });

        // Result - REMOVED MARKER as per request
        // We now show a popup overlay instead.
      });

      markers.sort((a, b) => a.time - b.time);
      series.setMarkers(markers);

      // Sync Price Lines
      priceLinesRef.current.forEach((line) => {
        // Clear all first or diff?
        // Simplest: clear all lines not in current trades
        // But clearing/re-adding flickers.
      });

      // Add new lines
      trades.forEach((t) => {
        if (t.status === "OPEN" && !priceLinesRef.current.has(t.betId)) {
          const line = series.createPriceLine({
            price: t.start_price,
            color: t.direction === "high" ? "#22c55e" : "#ef4444",
            lineWidth: 2,
            lineStyle: 2,
            axisLabelVisible: true,
            title: `Bet $${t.quantity}`,
          });
          priceLinesRef.current.set(t.betId, line);
        }
      });

      // Remove old lines
      const activeIds = new Set(
        trades.filter((t) => t.status === "OPEN").map((t) => t.betId),
      );
      for (const [id, line] of priceLinesRef.current.entries()) {
        if (!activeIds.has(id)) {
          series.removePriceLine(line);
          priceLinesRef.current.delete(id);
        }
      }
    }
  }, [trades]);

  useEffect(() => {
    // Timer Loop (Countdown)
    const clockInterval = setInterval(() => {
      const now = Date.now();
      const activeTrades = tradesRef.current.filter((t) => t.status === "OPEN");
      const isMob = window.innerWidth < 768; // Check directly to avoid stale state

      if (activeTrades.length > 0) {
        // Show countdown for the LATEST active trade (for React State UI if needed)
        const latest = activeTrades[activeTrades.length - 1];
        const endTime = latest.startTime + latest.duration * 1000;
        const leftMs = endTime - now;
        const leftSec = Math.max(0, Math.ceil(leftMs / 1000));

        const m = Math.floor(leftSec / 60);
        const s = leftSec % 60;
        setTimerDisplay(
          `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`,
        );

        // Update Price Lines with Timer
        activeTrades.forEach((t) => {
          const line = priceLinesRef.current.get(t.betId);
          if (line) {
            const tEndTime = t.startTime + t.duration * 1000;
            const tLeftMs = tEndTime - now;
            const tLeftSec = Math.max(0, Math.ceil(tLeftMs / 1000));

            // Only update if changed (optimization)
            // But title update is cheap.
            if (isMob) {
              line.applyOptions({
                title: `$${t.quantity} (${tLeftSec}s)`,
              });
            } else {
              // Ensure default on desktop
              line.applyOptions({
                title: `Bet $${t.quantity}`,
              });
            }
          }
        });
      } else {
        setTimerDisplay("00:00");
      }
    }, 200); // Update 5 times a second

    // Chart Init
    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: "transparent" },
        textColor: isMobile ? "#ffffff27" : "#ffffff",
        fontSize: isMobile ? 9 : 12, // User requested "small and neat"
      },
      grid: {
        vertLines: { color: "transparent" },
        horzLines: { color: "transparent" },
      },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      leftPriceScale: {
        visible: false,
      },
      rightPriceScale: {
        visible: true,
        borderVisible: false,
        scaleMargins: {
          top: 0.1, // Leave room for top items
          bottom: 0.1,
        },
        textColor: isMobile ? "#ffffff0a" : "white",
        width: isMobile ? 40 : 55, // Minimal width for price scale on mobile
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        shiftVisibleRangeOnNewBar: false, // PREVENT AUTO-SCROLL JUMP
        rightOffset: isMobile ? 2 : 20, // Minimal offset
        barSpacing: isMobile ? 4 : 6, // Small candles
        borderVisible: false,
      },
      attributionLogo: false, // Turn off TradingView logo
    });

    // FORCE Y-axis label spacing
    // chart.applyOptions({
    //     localization: {
    //         priceFormatter: (price) => {
    //             const STEP = 100;   // ← change spacing here
    //             return (Math.round(price / STEP) * STEP).toLocaleString();
    //         }
    //     }
    // });

    // --- INITIAL ZOOM ANIMATION ---
    const animateZoomToLast15 = () => {
      if (
        !chartRef.current ||
        !candleSeriesRef.current ||
        !candleSeriesRef.current.data
      )
        return;

      const timeScale = chartRef.current.timeScale();
      const data = candleSeriesRef.current.data();

      if (data.length > 15) {
        const total = data.length;
        // Target: Show [total-15, total]
        const startRange = timeScale.getVisibleLogicalRange();
        if (!startRange) return;

        const endFrom = total - 15;
        const endTo = total;

        // If we are already close, maybe skip? No, force animation for effect.

        const duration = 1000; // 1s animation
        const startTime = performance.now();

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3); // Cubic out

          const currFrom = startRange.from + (endFrom - startRange.from) * ease;
          const currTo = startRange.to + (endTo - startRange.to) * ease;

          timeScale.setVisibleLogicalRange({ from: currFrom, to: currTo });

          if (progress < 1) requestAnimationFrame(animate);
          else {
            timeScale.applyOptions({ rightOffset: 5 });
          }
        };
        requestAnimationFrame(animate);
      }
    };

    // Initial trigger
    setTimeout(() => {
      animateZoomToLast15();
    }, 3000);

    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length === 0 || entries[0].target !== containerRef.current)
        return;
      const newRect = entries[0].contentRect;
      chart.applyOptions({ width: newRect.width, height: newRect.height });
      setContainerHeight(newRect.height);
    });
    resizeObserver.observe(containerRef.current);

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#10863bff",
      downColor: "#c22d2dff",
      borderUpColor: "#10863bff",
      borderDownColor: "#c22d2dff",
      wickUpColor: "#10863bff",
      wickDownColor: "#c22d2dff",
    });
    candleSeriesRef.current = candleSeries;

    // 2. Load History
    const loadHistory = async () => {
      try {
        setIsLoading(true);
        const conf = INTERVALS[activeInterval];
        const apiInt = conf.apiInterval;
        let parsedCandles = [];

        if (tab?.typ === "forex") {
          // FOREX HISTORY FETCH
          const res = await Axios.post(
            "/users/getforexhistory",
            {
              pair: currencyPair,
              interval: 1, // HighChat uses 1 for backend compatibility
            },
            { headers: { Authorization: localStorage.getItem("nzanzi") } },
          );

          if (res.data && res.data.success && res.data.result) {
            const rawData = res.data.result;
            // Format: { datetime, open, high, low, close }
            // Filter invalid dates and sort
            const sorted = rawData
              .map((r) => {
                // Parse date similar to Forexchart.js
                // Trying simple parse first
                let tMs = Date.parse(r.datetime);
                if (isNaN(tMs)) tMs = Date.parse(r.datetime + "Z"); // Try UTC
                if (isNaN(tMs)) return null;

                return {
                  time: Math.floor(tMs / 1000), // CONVERT TO SECONDS
                  open: parseFloat(r.previous_close || r.open),
                  high: parseFloat(r.high),
                  low: parseFloat(r.low),
                  close: parseFloat(r.close),
                };
              })
              .filter(Boolean)
              .sort((a, b) => a.time - b.time);

            // Helper: Generate realistic 5s sub-candles from a 1m candle
            // Uses a simplified random walk that ensures High/Low are respected and ends at Close.
            const expandForexTo5s = (klines1m) => {
              const expanded = [];
              // Sort first
              klines1m.sort((a, b) => a.time - b.time);

              for (const k of klines1m) {
                // We need 12 bars (60s / 5s)
                const numBars = 12;
                const subCandles = [];

                // We need to generate a path of 12 internal close prices
                // 1. Start is k.open
                // 2. End is k.close
                // 3. Must touch k.high and k.low at least once

                // Simple logic:
                // Create 12 points.
                // Force one point to be High, one to be Low.
                // Others interpolated with some noise.

                let points = new Array(numBars).fill(0);

                // Random indices for High and Low peaks (avoid 0 if possible to allow Open)
                let highIdx = Math.floor(Math.random() * numBars);
                let lowIdx = Math.floor(Math.random() * numBars);

                // Assign key points
                points[0] = k.open; // First candle opens at k.open
                points[numBars - 1] = k.close; // Last candle closes at k.close

                // Simple linear interpolation for base "trend"
                for (let i = 1; i < numBars - 1; i++) {
                  const progress = i / (numBars - 1);
                  points[i] = k.open + (k.close - k.open) * progress;
                }

                // Apply noise and force High/Low
                // We'll iterate and adjust specific points to be High/Low
                points[highIdx] = k.high;
                points[lowIdx] = k.low;

                // Final Pass: Ensure consistency
                // We might have broken the path smoothness, but for 5s it's okay.
                // Construct candles
                for (let i = 0; i < numBars; i++) {
                  // Previous Close is current Open (except first)
                  const sOpen = i === 0 ? k.open : points[i - 1];
                  const sClose = points[i];

                  // Random wicks for sub-candles, constrained by overall High/Low
                  const volatility = Math.abs(k.high - k.low) * 0.1;
                  const rHigh = Math.min(
                    k.high,
                    Math.max(sOpen, sClose) + Math.random() * volatility,
                  );
                  const rLow = Math.max(
                    k.low,
                    Math.min(sOpen, sClose) - Math.random() * volatility,
                  );

                  subCandles.push({
                    time: k.time + i * 5, // Time offset
                    open: sOpen,
                    high: rHigh,
                    low: rLow,
                    close: sClose,
                  });
                }

                // CLAMP: Remove future candles
                const nowSec = Math.floor(Date.now() / 1000);
                const validSubCandles = subCandles.filter(
                  (s) => s.time <= nowSec,
                );
                expanded.push(...validSubCandles);
              }
              return expanded;
            };

            if (conf.isCustom && activeInterval === "5s") {
              // Expand with realistic structure
              parsedCandles = expandForexTo5s(sorted);
            } else {
              // Standard: Already in seconds
              parsedCandles = sorted;
            }
          }
        } else {
          // CRYPTO (BINANCE) HISTORY FETCH
          const res = await fetch(
            `https://api.binance.com/api/v3/klines?symbol=${currencyPair}&interval=${apiInt}&limit=1000`,
          );
          const data = await res.json();
          // const startTime = Date.now() - 86400000;
          // const endTime = Date.now();
          // const { data: response } = await Axios.post("/get-binance-kline", {
          //   category: "spot",
          //   symbol: currencyPair,
          //   start: startTime,
          //   end: endTime,
          //   interval: apiInt === "1s" ? "1" : apiInt,
          // });
          // const data = response.result.reverse();
          if (conf.isCustom && activeInterval === "5s") {
            parsedCandles = aggregate1sTo5s(data);

            // SYNC BUFFER: Populate active1sCandlesRef with the 1s klines
            // that belong to the *last* 5s candle we just created.
            if (parsedCandles.length > 0) {
              const lastCandle = parsedCandles[parsedCandles.length - 1];
              const lastBucketTime = lastCandle.time; // Seconds
              console.log("lastBucketTime", lastBucketTime);
              const bufferKlines = data
                .filter((k) => {
                  const tMs = k[0];
                  const tSec = Math.floor(tMs / 1000);
                  const bucket = Math.floor(tSec / 5) * 5;
                  return bucket === lastBucketTime;
                })
                .map((k) => ({
                  t: k[0],
                  o: parseFloat(k[1]),
                  h: parseFloat(k[2]),
                  l: parseFloat(k[3]),
                  c: parseFloat(k[4]),
                }));

              active1sCandlesRef.current = bufferKlines;
            }
          } else {
            parsedCandles = data.map((k) => ({
              time: Math.floor(k[0] / 1000),
              open: parseFloat(k[1]),
              high: parseFloat(k[2]),
              low: parseFloat(k[3]),
              close: parseFloat(k[4]),
            }));
          }
        }
        // Set data
        if (candleSeriesRef.current) {
          candleSeriesRef.current.setData(parsedCandles);

          // Reset Real Data Refs
          if (parsedCandles.length > 0) {
            const last = parsedCandles[parsedCandles.length - 1];
            currentCandleRealDataRef.current = { ...last };
            animPriceRef.current = last.close;
            targetPriceRef.current = last.close;
          }

          // Force Zoom
          setTimeout(() => animateZoomToLast15(), 50);

          // Dynamic Precision: Adjust decimals based on price magnitude
          if (parsedCandles.length > 0) {
            const lastClose = parsedCandles[parsedCandles.length - 1].close;
            let precision = 2;
            let minMove = 0.01;

            if (lastClose < 0.001) {
              precision = 7;
              minMove = 0.0000001;
            } else if (lastClose < 1) {
              precision = 6;
              minMove = 0.000001;
            } else if (lastClose < 20) {
              precision = 4; // Forex usually 4-5
              minMove = 0.0001;
            } else if (tab?.typ === "forex") {
              // Force higher precision for forex pairs like JPY if needed,
              // but usually < 20 covers it (e.g. EURUSD 1.05).
              // JPY pairs are ~150.00, so precision 2 is fine?
              // Actually JPY pairs usually need 3 decimals (145.123).
              // Standard pairs need 5 (1.05123).
              if (lastClose > 100) {
                precision = 3;
                minMove = 0.001;
              } else {
                precision = 5;
                minMove = 0.00001;
              }
            }

            candleSeries.applyOptions({
              priceFormat: {
                type: "price",
                precision: precision,
                minMove: minMove,
              },
            });
          }
        }

        if (parsedCandles.length > 0) {
          const last = parsedCandles[parsedCandles.length - 1];
          targetPriceRef.current = last.close;
          animPriceRef.current = last.close;
          currentCandleRealDataRef.current = { ...last };
        }

        chart.timeScale().scrollToPosition(0, true);

        // Trigger Zoom Animation on new data load (Interval Change)
        setTimeout(() => {
          animateZoomToLast15();
        }, 500);
      } catch (err) {
        console.error("Failed to load history:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();

    // --------------------------------------------------------
    // SMOOTH SCROLL Logic
    // --------------------------------------------------------
    const smoothScrollToNext = () => {
      const scale = chart.timeScale();
      // We want to shift the valid range to the left by 1 bar width equivalent
      // Actually 'scrollToRealTime' works but can be jumpy.
      // Let's use setVisibleLogicalRange and animate 'to' value.

      const range = scale.getVisibleLogicalRange();
      if (!range) return;

      // Target: shift +1 logical index
      const from = range.from + 1;
      const to = range.to + 1;

      const startTime = performance.now();
      const duration = 1000; // 1 second scroll duration

      const animateScroll = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const ease = 1 - Math.pow(1 - progress, 3);

        const currentFrom = range.from + (from - range.from) * ease;
        const currentTo = range.to + (to - range.to) * ease;

        scale.setVisibleLogicalRange({ from: currentFrom, to: currentTo });

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      requestAnimationFrame(animateScroll);
    };

    // --------------------------------------------------------
    // HELPER: Process Incoming Tick (Crypto or Forex)
    // --------------------------------------------------------
    const processTick = (timeMs, o, h, l, c) => {
      const conf = INTERVALS[activeInterval];

      // Update TARGET price for animation
      targetPriceRef.current = c;
      // If we haven't initialized animPrice, snap it
      if (animPriceRef.current === null) animPriceRef.current = c;

      const t = Math.floor(timeMs / 1000);

      // -------------------------------------------------------------
      // CUSTOM (5s) LOGIC
      // -------------------------------------------------------------
      if (conf.isCustom && activeInterval === "5s") {
        // We bucket into 5s intervals
        const bucketTime = Math.floor(t / 5) * 5;

        // GUARD: Monotonic Time Check
        // If this tick is for an older bucket than we know about, ignore it.
        if (
          currentCandleRealDataRef.current &&
          currentCandleRealDataRef.current.time > bucketTime
        ) {
          return;
        }

        let activeKlines = active1sCandlesRef.current;
        let isNewBucket = false;

        // Check if we moved to a new bucket
        if (activeKlines.length > 0) {
          const lastK = activeKlines[0];
          const lastTimeMs = lastK.t;
          const lastBucket = Math.floor(lastTimeMs / 1000 / 5) * 5;
          if (bucketTime > lastBucket) {
            isNewBucket = true;
            activeKlines = [];
          }
        }

        // Dedupe/Update current second
        // For Crypto (Binance), we get updates for the same second.
        // For Forex (Polled), we might get same timestamp multiple times.
        const existingIdx = activeKlines.findIndex(
          (item) => Math.floor(item.t / 1000) === t,
        );
        if (existingIdx >= 0) {
          activeKlines[existingIdx] = { t: timeMs, o, h, l, c };
        } else {
          activeKlines.push({ t: timeMs, o, h, l, c });
        }
        active1sCandlesRef.current = activeKlines;

        if (activeKlines.length === 0) return;

        if (isNewBucket) {
          const newCandle = {
            time: bucketTime,
            open: o,
            high: h,
            low: l,
            close: c,
          };
          currentCandleRealDataRef.current = { ...newCandle }; // Sync Real
          targetPriceRef.current = c; // Updates Animation Target
          if (candleSeriesRef.current) {
            candleSeriesRef.current.update(newCandle);
            smoothScrollToNext();
          }
        } else {
          // Re-aggregate from buffer to get true O/H/L
          let aggO = activeKlines[0].o;
          let aggH = -Infinity;
          let aggL = Infinity;
          for (const k1 of activeKlines) {
            aggH = Math.max(aggH, k1.h);
            aggL = Math.min(aggL, k1.l);
          }

          // update Real
          currentCandleRealDataRef.current = {
            time: bucketTime,
            open: aggO,
            high: aggH,
            low: aggL,
            // We DO NOT update series here to avoid flicker.
          };
        } // End 5s logic
      } else {
        // -------------------------------------------------------------
        // STANDARD INTERVAL LOGIC (1m, 3m, etc.)
        // -------------------------------------------------------------
        const candleTime = Math.floor(timeMs / 1000);

        // Round candleTime to nearest interval just to be safe?
        // Binance klines usually come with logical time attached.
        // But for Forex polling, we assume current time belongs to current bucket.
        // To make it robust for standard intervals:
        const intervalSec = (() => {
          if (conf.apiInterval.endsWith("m"))
            return parseInt(conf.apiInterval) * 60;
          if (conf.apiInterval.endsWith("h"))
            return parseInt(conf.apiInterval) * 3600;
          if (conf.apiInterval.endsWith("d"))
            return parseInt(conf.apiInterval) * 86400;
          return 60;
        })();

        const bucketStart = Math.floor(candleTime / intervalSec) * intervalSec;

        // GUARD: Monotonic Time Check
        if (
          currentCandleRealDataRef.current &&
          currentCandleRealDataRef.current.time > bucketStart
        ) {
          return;
        }

        const data = candleSeriesRef.current
          ? candleSeriesRef.current.data()
          : [];
        let isNewBar = true;
        if (data.length > 0) {
          const lastBar = data[data.length - 1];
          if (lastBar.time === bucketStart) isNewBar = false;
        }

        // Standard Mode: Sync Real Data
        if (isNewBar) {
          currentCandleRealDataRef.current = {
            time: bucketStart,
            open: o,
            high: h,
            low: l,
            close: c,
          };
          targetPriceRef.current = c; // Updates Animation Target
        } else {
          const r = currentCandleRealDataRef.current;
          currentCandleRealDataRef.current = {
            time: bucketStart,
            open: r.open,
            high: Math.max(r.high, h),
            low: Math.min(r.low, l),
            close: c,
          };
          targetPriceRef.current = c; // Updates Animation Target
        }

        if (candleSeriesRef.current) {
          candleSeriesRef.current.update({
            time: bucketStart,
            open: currentCandleRealDataRef.current.open,
            high: currentCandleRealDataRef.current.high,
            low: currentCandleRealDataRef.current.low,
            close: animPriceRef.current, // Visual close used for animation
          });
        }

        if (isNewBar) smoothScrollToNext();
      }
    };

    // --------------------------------------------------------
    // ANIMATION LOOP (Price + Pulse + Noise)
    // --------------------------------------------------------
    const tick = () => {
      const realTarget = targetPriceRef.current;
      let current = animPriceRef.current;

      if (realTarget !== null && current !== null) {
        // --- FAKE ACTIVITY (Jitter) ---
        // SMOOTH ORGANIC MOVEMENT (Inertia/Momentum)
        // Instead of random jumping, we use acceleration -> velocity -> position

        let noise = noiseRef.current;
        let velocity = noiseVelocityRef.current;

        // 1. Calculate Max Noise and Acceleration Scale based on Price
        // Fixed Proportional Approach (0.005%)
        // BTC @ 100k -> +/- 5.0 points
        // EUR @ 1.05 -> +/- 0.00005 points
        const volatility = 0.00005; // 0.005%
        let maxNoise = realTarget * volatility;

        // Acceleration should be gentle.
        // Let's say 2% of maxNoise per frame max change.
        const accelScale = maxNoise * 0.02;

        // 2. Random Acceleration (Wind)
        const accel = (Math.random() - 0.5) * accelScale;

        // 3. Update Velocity with Inertia
        velocity += accel;
        velocity *= 0.95; // Higher friction for smooth dampening

        // 4. Update Position (Noise)
        noise += velocity;

        // 5. Gravity (Center pull) - gentle return
        noise *= 0.98;

        // 6. CLAMP
        if (noise > maxNoise) noise = maxNoise;
        if (noise < -maxNoise) noise = -maxNoise;

        // Store state
        noiseVelocityRef.current = velocity;
        noiseRef.current = noise;

        // Visual Target is real price + noise
        const visualTarget = realTarget + noise;

        // Smooth factor 0.1
        const diff = visualTarget - current;
        // Always animate if noise exists, even if diff is small
        if (Math.abs(diff) > 0.0000001) {
          current = current + diff * 0.1;
          animPriceRef.current = current;

          // Update the LAST candle in the series with this smoothed price
          if (candleSeriesRef.current) {
            const data = candleSeriesRef.current.data();
            if (data.length > 0) {
              const last = data[data.length - 1];

              // READ REAL DATA
              const real = currentCandleRealDataRef.current;
              const updateTime = real.time || last.time;

              // GUARD: Never update with older time
              if (updateTime < last.time) {
                // This implies real data lagged behind chart state.
                // Should fix itself on next tick, but abort this update.
                return;
              }

              // Visual Calculations
              let vClose = current;
              let vOpen = real.open || last.open;
              // Expand Real High/Low with Visual Noise
              let vHigh = Math.max(real.high || last.high, vClose);
              let vLow = Math.min(real.low || last.low, vClose);

              // Note: We do NOT force spikes or min body here,
              // the noise itself provides the "activity".

              candleSeriesRef.current.update({
                ...last,
                time: updateTime,
                close: vClose,
                open: vOpen,
                high: vHigh,
                low: vLow,
              });

              // -------------------------------------------------------------
              // AUTO-SCALE Y-AXIS IF PRICE IS OUT OF VIEW
              // -------------------------------------------------------------
              if (
                chartRef.current &&
                containerRef.current &&
                candleSeriesRef.current
              ) {
                const y = candleSeriesRef.current.priceToCoordinate(vClose);
                const height = containerRef.current.clientHeight;

                // If y is null, it's technically off-scale/invalid, but usually it returns a value.
                // If y < 0 (above chart) or y > height (below chart), it's out of view.
                if (y !== null) {
                  if (y < 0 || y > height) {
                    chartRef.current.priceScale("right").applyOptions({
                      autoScale: true,
                    });
                  }
                }
              }
            }
          } // end data length check
        } // End Visual/Noise Update

        // -------------------------------------------------------------
        // HEARTBEAT for FOREX 5s (Keep Alive)
        // -------------------------------------------------------------
        const conf = INTERVALS[activeInterval];
        if (tab?.typ === "forex" && conf.isCustom && activeInterval === "5s") {
          const nowSec = Math.floor(Date.now() / 1000);
          const expectedBucket = Math.floor(nowSec / 5) * 5;
          const lastReal = currentCandleRealDataRef.current;

          if (lastReal && expectedBucket > lastReal.time) {
            // We lagged behind! Create a filler candle.
            const fillerCandle = {
              time: expectedBucket,
              open: lastReal.close,
              high: lastReal.close,
              low: lastReal.close,
              close: lastReal.close,
            };

            // Update Real Ref
            currentCandleRealDataRef.current = fillerCandle;

            // Reset active buffer
            active1sCandlesRef.current = [];

            // Update Chart
            if (candleSeriesRef.current) {
              candleSeriesRef.current.update(fillerCandle);
              smoothScrollToNext();
            }

            // Set anim target to match
            targetPriceRef.current = lastReal.close;
          }
        }

        // -------------------------------------------------------------
        // HIGH FREQUENCY EXPIRY CHECK (60fps)
        // -------------------------------------------------------------
        const now = Date.now();
        // Use ref to avoid stale closure
        const activeTrades = tradesRef.current;

        activeTrades.forEach((t) => {
          if (t.status === "OPEN" && now >= t.endTime) {
            // Prevent double processing
            if (!processingBetsRef.current.has(t.betId)) {
              processingBetsRef.current.add(t.betId);
              finishBet(t);
            }
          }
        });

        // FIX: Pulse should update every frame if valid,
        // especially with noise.

        // --- UPDATE PULSE POSITION ---
        if (pulseRef.current && candleSeriesRef.current) {
          const data = candleSeriesRef.current.data();
          if (data.length > 0) {
            const last = data[data.length - 1];
            const time = last.time;

            // Pulse must follow the VISUAL Close (vClose from above)
            // But 'vClose' is local scope.
            // We need to re-calculate or access it.
            // Best way: Read 'close' from the last data point we just updated!
            const price = last.close;

            const timeScale = chart.timeScale();
            const x = timeScale.timeToCoordinate(time);
            const y = candleSeriesRef.current.priceToCoordinate(price);

            if (x !== null && y !== null) {
              pulseRef.current.style.display = "block";
              pulseRef.current.style.transform = `translate(${x - 5}px, ${y - 5
                }px)`;
            } else {
              pulseRef.current.style.display = "none";
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // 3. Connect WebSocket (Only if NOT Forex)
    let ws = null;

    let forexInterval = null;

    if (tab?.typ === "forex") {
      // FOREX WEBSOCKET (TwelveData)
      const forexpair = tab.title.includes("/")
        ? tab.title.toUpperCase()
        : `${tab.title.slice(0, 3).toUpperCase()}/${tab.title
          .slice(3)
          .toUpperCase()}`;

      // Initial connection
      ws = new WebSocket(
        `wss://ws.twelvedata.com/v1/quotes/price?apikey=${consts.apikey}`,
      );
      socketRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            action: "subscribe",
            params: { symbols: forexpair },
          }),
        );
      };

      // Keep-alive ping
      forexInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: "ping" }));
        }
      }, 30000);

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (!data?.symbol || !data?.price) return;

          // Filter for our pair (just in case)
          // data.symbol might come as "EUR/USD" or "EURUSD" depending.
          // We subscribed with explicit slash usually.
          // But let's just assume it's the right one.

          const price = parseFloat(data.price);
          const timestamp = data.timestamp * 1000; // seconds to ms

          // Update Price Ref for other logic
          const spreadSize = data.symbol.includes("JPY") ? 0.001 : 0.00001;
          const bid = price - spreadSize / 2;
          const ask = price + spreadSize / 2;
          priceRef.current[currencyPair] = {
            midprice: price,
            buy: +ask.toFixed(5),
            sell: +bid.toFixed(5),
            timestamp: data.timestamp,
          };

          // Update UI Buttons (Mirroring HighChat)
          const symbolFormatted = data.symbol.replace("/", "");
          const spread = spreadSize.toFixed(5);
          $(`.buybtn-${symbolFormatted}`).text(ask.toFixed(5));
          $(`.sellbtn-${symbolFormatted}`).text(bid.toFixed(5));
          $(`.spreadchart-${symbolFormatted}`).text(String(spread));

          // Process Tick for Chart
          // We treat the tick as a "flat" candle update.
          // processTick aggregates it into the current bar (maintaining High/Low).
          processTick(timestamp, price, price, price, price);
        } catch (err) {
          console.error("Forex WS Error", err);
        }
      };
    } else {
      ws = new WebSocket("wss://stream.binance.com:9443/ws");
      socketRef.current = ws;

      const conf = INTERVALS[activeInterval];
      const topic = `${currencyPair.toLowerCase()}@kline_${conf.apiInterval}`;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            method: "SUBSCRIBE",
            params: [topic],
            id: 1,
          }),
        );
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          console.log("msg", msg);
          if (msg.e !== "kline" || !msg.k) return;

          const k = msg.k;

          const startTimeMs = Number(k.t);
          const o = parseFloat(k.o);
          const h = parseFloat(k.h);
          const l = parseFloat(k.l);
          const c = parseFloat(k.c);

          processTick(startTimeMs, o, h, l, c);
        } catch (err) {
          console.error("WS Error", err);
        }
      };
    }

    return () => {
      resizeObserver.disconnect();
      if (ws) ws.close();
      if (forexInterval) clearInterval(forexInterval);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (autoScrollTimerRef.current) clearTimeout(autoScrollTimerRef.current);
      chart.remove();
      clearInterval(clockInterval);
    };
  }, [activeInterval, tab?.typ, isMobile]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.timeScale().fitContent();
      }
    }, 100);
    return () => clearTimeout(t);
  }, [activeInterval]);

  // Layout State for Responsive
  const [containerHeight, setContainerHeight] = useState(600); // Default to decent size

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Profit / Payout Calculation
  const payout = (quantity * percent) / 100;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        width: "100%",
        height: "100%",
        // background: "#131722", // match chart bg
        overflow: "hidden",
      }}
    >
      <div className="mobChart-header">
        <div className="mb-2 ">
          <div className="display-5 gap-5 framer-crd">
            <div style={{ width: "100%" }}>
              <Card
                style={{
                  background: "rgb(39 42 50)",
                  borderRadius: 8,
                  padding: 8,
                  // width: ,
                  color: "white",
                  minHeight: "64px",
                }}
              >
                <div className="display-2 gap-10">
                  <div>
                    <div style={{ fontSize: 12, color: "#a7a7a7" }}>Asset</div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: "10px",
                      }}
                    >
                      <img
                        src={tab?.imgSrc}
                        alt={"row.title"}
                        width={25}
                        height={25}
                      />

                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: "white",
                        }}
                        className="asset-prf"
                      >
                        {currencyPair ? currencyPair.toUpperCase() : ""}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, color: "#a7a7a7" }}>Profit</div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#00ff80",
                        marginTop: "10px",
                      }}
                      className="asset-prf"
                    >
                      {percent}%
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div style={{ width: "100%" }}>
              <Card
                style={{
                  background: "rgb(39 42 50)",
                  borderRadius: 8,
                  padding: 8,
                  // width: ,
                  color: "white",
                  minHeight: "64px",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div className="">Time Frame</div>

                <div className="display-3 gap-10 mt-5">
                  {Object.keys(INTERVALS).map((k) => (
                    <div
                      style={{
                        background:
                          activeInterval === k
                            ? "rgb(255 204 0 / 71%)"
                            : "#000",
                        padding: "6px 14px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        color: activeInterval === k ? "#fff" : "#ccc",
                        cursor: "pointer",
                        opacity: activeInterval === k ? 1 : 0.5,
                        flexDirection: "row",
                      }}
                      className="time-framer display-3 gap-15"
                      key={k}
                      onClick={() => setActiveInterval(k)}
                    >
                      {INTERVALS[k].label}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
        <div className="display-5 mt-10">
          <div style={{ fontSize: 12, color: "#a7a7a7" }}>
            Estimated Profit{" "}
            <span style={{ color: "#22c55e", fontWeight: "600", fontSize: 12 }}>
              {" "}
              +${payout.toFixed(2)} {percent}%{" "}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#a7a7a7" }}>
            price <span style={{ color: "#fff" }}>$92412</span>{" "}
          </div>
        </div>
      </div>
      {/* CHART AREA */}
      <div
        style={{
          flex: 1,
          position: "relative",
          height: "100%",
          minHeight: 0, // Flex child scroll fix
        }}
      >
        {/* Top Left: Symbol Info */}
        {screens.md && (
          <div
            style={{
              position: "absolute",
              top: 15,
              left: 15,
              zIndex: 20,
              display: "flex",
              alignItems: "center", // Align image and text block
              pointerEvents: "none",
              gap: "10px",
            }}
          >
            {/* Pair Image */}
            {tab?.imgSrc && (
              <img
                src={tab?.imgSrc}
                alt={tab?.imgSrc}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )}

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}
              >
                {tab?.typ === "forex"
                  ? currencyPair.toUpperCase()
                  : currencyPair}
              </div>
              <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                {tab?.typ?.toUpperCase() || "CRYPTO"}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Left: Interval Dropdown */}
        <div
          style={{
            position: "absolute",
            bottom: 40, // Above time scale
            left: 15,
            zIndex: 20,
          }}
          className="time-framedesk"
        >
          <div
            style={{
              display: "flex",
              background: "rgba(19, 23, 34, 0.8)", // Semi-transparent
              borderRadius: 4,
              border: "1px solid #2a2e39",
              padding: "0 5px",
              alignItems: "center",
              backdropFilter: "blur(4px)",
            }}
          >
            <select
              value={activeInterval}
              onChange={(e) => setActiveInterval(e.target.value)}
              style={{
                background: "transparent",
                color: "#d1d4dc",
                border: "none",
                padding: "6px 20px 6px 6px", // Extra right padding for arrow
                fontSize: "12px",
                outline: "none",
                cursor: "pointer",
                appearance: "none", // Remove default arrow in some browsers
                fontWeight: "bold",
              }}
            >
              {Object.keys(INTERVALS).map((k) => (
                <option
                  key={k}
                  value={k}
                  style={{ background: "#131722", color: "#d1d4dc" }}
                >
                  {INTERVALS[k].label}
                </option>
              ))}
            </select>
            {/* Custom Arrow Icon */}
            <div
              style={{
                marginLeft: -15,
                pointerEvents: "none",
                color: "#9ca3af",
                fontSize: 10,
              }}
            >
              ▼
            </div>
          </div>
        </div>
        {/* Top Right Ticker (Countdown) */}
        {/* <div style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    padding: '5px 10px',
                    background: '#1e293b',
                    color: timerDisplay === "00:00" ? '#9ca3af' : '#22c55e',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 20,
                    minWidth: '60px',
                    textAlign: 'center',
                    border: '1px solid #334155'
                }}>
                    {timerDisplay}
                </div> */}

        {/* Pulse Element */}
        <style>
          {`
         @keyframes pulse-animation {
           0% {
             box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.8);
           }
           70% {
             box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
           }
           100% {
             box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
           }
         }
         .pulse-dot {
           width: 10px;
           height: 10px;
           background-color: #22c55e;
           border-radius: 50%;
           position: absolute;
           top: 0;
           left: 0;
           z-index: 20;
           pointer-events: none;
           animation: pulse-animation 2s infinite;
           will-change: transform;
         }
       `}
        </style>
        <div ref={pulseRef} className="pulse-dot" style={{ display: "none" }} />

        {/* Result Popup Overlay */}
        {resultPopup && (
          <div
            style={{
              position: "absolute",
              top: "20%",
              left: "50%",
              transform: "translate(-50%, 0)",
              zIndex: 100,
              background: "rgba(19, 23, 34, 0.95)",
              border: resultPopup.win
                ? "2px solid #22c55e"
                : "2px solid #ef4444",
              borderRadius: "12px",
              padding: "20px 40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
              backdropFilter: "blur(8px)",
              animation: "fadeIn 0.3s ease-out",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              {/* Flags instead of Trophy/Heart */}
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "10px",
                  color: resultPopup.win ? "#22c55e" : "#ef4444",
                  display: "flex",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill={resultPopup.win ? "#22c55e" : "#ef4444"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                  <line x1="4" y1="22" x2="4" y2="15"></line>
                </svg>
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: resultPopup.win ? "#22c55e" : "#ef4444",
                  textTransform: "uppercase",
                  marginBottom: "5px",
                }}
              >
                {resultPopup.win ? "You Won" : "You Lost"}
              </div>
            </div>
            <div
              style={{
                fontSize: "18px",
                color: "#fff",
                fontWeight: "500",
              }}
            >
              {resultPopup.win ? "+" : "-"}$
              {Math.abs(resultPopup.amount).toFixed(2)}
            </div>
            <style>{`
                            @keyframes fadeIn {
                                from { opacity: 0; transform: translate(-50%, 10px); }
                                to { opacity: 1; transform: translate(-50%, 0); }
                            }
                        `}</style>
          </div>
        )}

        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

        {/* Loading Overlay */}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(19, 23, 34, 0.92)",
              zIndex: 50,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(2px)",
            }}
          >
            <div
              className="spinner"
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid rgba(255, 255, 255, 0.1)",
                borderTop: "4px solid #2962ff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
            <span
              style={{ marginTop: "10px", color: "#B2B5BE", fontSize: "12px" }}
            >
              Loading Chart...
            </span>
          </div>
        )}
      </div>

      {/* MOBILE STATIC CONTROLS (Below Chart) */}
      {isMobile && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6, // Compact gap
            width: "100%",
            // padding: '6px', // Compact padding
            background: "transparent",
            // borderTop: '1px solid #2a2e39',
            zIndex: 50,
          }}
        >
          {/* Row 1: Inputs & Profit */}
          <div
            style={{
              display: "flex",
              gap: 6,
              justifyContent: "space-between",
              // background: "rgba(30,34,45, 0.9)",
              // padding: 6, // Compact inner padding
              borderRadius: 8,
              marginBottom: 6,
              // border: "1px solid #2a2e39"
            }}
          >
            {/* <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                            <span style={{ fontSize: 14, color: '#9ca3af', textAlign: "center" }}>Amount</span>
                            <input
                                type="number"
                                value={quantity}
                                onChange={(e) => setQuantity(Number(e.target.value))}
                                style={{ background: '#13172217', fontSize: 14, textAlign: 'center', border: '1px solid #f7a50065', color: '#fff', borderRadius: 4, padding: '8px', width: '100%', outline: 'none' }}
                            />

                        </div> */}

            <div style={{ width: "100%" }}>
              <div
                style={{
                  // background: "#0f0f0f",
                  borderRadius: 4,
                  padding: "6px",
                  border: "1px solid #f7a50065",
                  maxHeight: "55px",
                  minHeight: "55px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <span
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                      textAlign: "center",
                    }}
                  >
                    Invest
                  </span>
                </div>
                <div
                  style={{
                    // background: "#1a1a1a",
                    // padding: "0px 6px",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // width: "100%",
                    gap: 6,
                  }}
                >
                  {/* MINUS BUTTON */}
                  <button
                    onClick={dec}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "#111",
                      border: "1px solid #333",
                      color: "white",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    –
                  </button>

                  {/* CENTER INPUT */}
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const val = e.target.value;

                      // If input is cleared → set empty string
                      if (val === "") {
                        setQuantity("");
                        return;
                      }

                      // If typing normal numbers → remove leading zeros automatically
                      const cleaned = val.replace(/^0+(?=\d)/, "");

                      setQuantity(cleaned);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "white",
                      width: 40,
                      textAlign: "center",
                      fontSize: 14,
                      outline: "none",
                    }}
                  />

                  {/* PLUS BUTTON */}
                  <button
                    onClick={inc}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "#111",
                      border: "1px solid #333",
                      color: "white",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                            <span style={{ fontSize: 14, color: '#9ca3af', textAlign: "center" }}>Time</span>
                            <select
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                style={{
                                    background: '#13172217',
                                    border: '1px solid #f7a50065',
                                    color: '#fff',
                                    borderRadius: 4,
                                    padding: '8px',
                                    width: '100%',
                                    outline: 'none',
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                    textAlign: 'center',
                                    textAlignLast: 'center', // IMPORTANT for iOS
                                    fontSize: 14,
                                }}
                            >
                                {DURATION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div> */}
            <div style={{ width: "100%" }}>
              <div
                style={{
                  borderRadius: 4,
                  padding: "6px",
                  border: "1px solid #f7a50065",
                  maxHeight: "55px",
                  // background: "#0f0f0f",
                  minHeight: "55px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>Time</span>
                </div>

                <div
                  style={{
                    // background: "#0f0f0f",
                    // padding: "8px 12px",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // width: "100%",
                    gap: 6,
                  }}
                >
                  {/* Minus button */}
                  <button
                    onClick={decre}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "#111",
                      border: "1px solid #333",
                      color: "white",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    –
                  </button>

                  {/* Current duration label */}
                  <span style={{ color: "white", fontSize: 16, width: 30 }}>
                    {DURATION_OPTIONS2[currentIndex].label}
                  </span>

                  {/* Plus button */}
                  <button
                    onClick={incre}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "#111",
                      border: "1px solid #333",
                      color: "white",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            {/* <div style={{ width: "100%" }}>

                                <div
                                    style={{
                                        borderRadius: 4,
                                        padding: "4px",
                                        border: '1px solid #f7a50065',
                                        maxHeight: "55px",
                                        // background: "#0f0f0f",
                                        minHeight: "55px",


                                    }}
                                >
                                    <div style={{ textAlign: "center" }}>

                                        <span style={{ fontSize: 11, color: "#9ca3af" }}>Buy in one click</span>
                                    </div>
                                   
                                    <div className="buy-click-swt ">
                                        <Switch size="small" />
                                    </div>

                                </div>
                            </div> */}

            {/* <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af' }}>Timer</span>
                            <span style={{
                                color: timerDisplay === "00:00" ? '#9ca3af' : '#22c55e',
                                fontWeight: 'bold',
                                fontSize: '12px' // Smaller timer font
                            }}>
                                {timerDisplay}
                            </span>
                        </div> */}
          </div>

          {/* Row 2: Buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              disabled={isBettingDisabled}
              onClick={() => handleBet("low")}
              style={{
                flex: 1,
                background: isBettingDisabled ? "#555" : "#ef4444",
                color: "#fff",
                border: "none",
                padding: "10px",
                borderRadius: 6,
                fontWeight: "bold",
                fontSize: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="display-3 gap-5">
                PUT{" "}
                <div>
                  {" "}
                  <img src={down} alt="down" width={12} height={12} />{" "}
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: "10px" }}>
                {" "}
                {percent}% of profit <br />{" "}
                <span style={{ fontSize: "8px" }}>
                  if rate falls after 10sec
                </span>
              </div>
            </button>
            <button
              disabled={isBettingDisabled}
              onClick={() => handleBet("high")}
              style={{
                flex: 1,
                background: isBettingDisabled ? "#555" : "#22c55e",
                color: "#fff",
                border: "none",
                padding: "10px",
                borderRadius: 6,
                fontWeight: "bold",
                fontSize: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "left", fontSize: "10px" }}>
                {percent}% of profit <br />{" "}
                <span style={{ fontSize: "8px" }}>
                  if rate falls after 10sec
                </span>
              </div>
              <div className="display-3 gap-5">
                {" "}
                <div>
                  {" "}
                  <img src={up} alt="down" width={12} height={12} />
                </div>
                CALL
              </div>
            </button>
          </div>

          {/* <div style={{ display: 'flex', flexDirection: 'row', gap: 2, flex: 1, alignItems: 'flex-end', justifyContent: 'center' }}>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>Estimated Profit {percent}% {`=>`}</span>
                        <span style={{ color: '#22c55e', fontWeight: '600', fontSize: 12, }}>+${payout.toFixed(2)}</span>
                    </div> */}
        </div>
      )}

      {/* CONTROLS SIDEBAR (Desktop Only) */}
      {!isMobile && (
        <div
          style={{
            width: "150px", // User customized width
            height: "100%",
            background: "transparent",
            borderLeft: "1px solid #transparent",
            display: "flex",
            flexDirection: "column",
            padding: containerHeight < 450 ? "5px" : "15px", // Compact Padding
            gap: containerHeight < 450 ? "5px" : "15px", // Compact Gap
            zIndex: 30,
            overflowY: "auto", // Allow scroll if absolute worst case
            fontSize: containerHeight < 450 ? "11px" : "14px", // Base font
          }}
        >
          {/* Timer Display */}
          <div
            style={{
              color: timerDisplay === "00:00" ? "#9ca3af" : "#22c55e",
              fontSize: containerHeight < 450 ? "14px" : "18px", // Compact Font
              fontWeight: "bold",
              textAlign: "center",
              background: "#131722",
              padding: containerHeight < 450 ? "5px" : "10px",
              borderRadius: "8px",
              border: "1px solid #363a45",
            }}
          >
            {timerDisplay}
          </div>

          {/* Amount Input */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              style={{
                color: "#9ca3af",
                fontSize: containerHeight < 450 ? 10 : 13,
              }}
            >
              Amount ($)
            </span>
            <div
              style={{
                display: "flex",
                background: "#131722",
                borderRadius: 4,
                border: "1px solid #363a45",
                padding: "0 10px",
                alignItems: "center",
              }}
            >
              <span style={{ color: "#fff", fontSize: 14 }}>$</span>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                style={{
                  background: "transparent",
                  color: "white",
                  border: "none",
                  width: "100%",
                  padding: containerHeight < 450 ? "6px" : "10px",
                  textAlign: "left",
                  fontSize: containerHeight < 450 ? "12px" : "14px",
                  outline: "none",
                }}
              />
            </div>
          </div>

          {/* Time Selection */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span
              style={{
                color: "#9ca3af",
                fontSize: containerHeight < 450 ? 10 : 13,
              }}
            >
              Time
            </span>
            <div
              style={{
                display: "flex",
                background: "#131722",
                borderRadius: 4,
                border: "1px solid #363a45",
                padding: "0 5px",
                alignItems: "center",
              }}
            >
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={{
                  background: "transparent",
                  color: "white",
                  border: "none",
                  width: "100%",
                  padding: containerHeight < 450 ? "6px" : "10px",
                  textAlign: "left",
                  fontSize: containerHeight < 450 ? "12px" : "14px",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    style={{ background: "#131722", color: "white" }}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Profit / Payout Display - Compact Mode Redesign */}
          {containerHeight >= 350 && ( // Hide profit entirely if ultra short, show in buttons maybe?
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                alignItems: "center",
                background: "#131722",
                padding: containerHeight < 450 ? "5px" : "10px",
                gap: 2,
                borderRadius: 4,
                marginTop: 5,
              }}
            >
              <span
                style={{
                  color: "#9ca3af",
                  fontSize: containerHeight < 450 ? 11 : 16,
                }}
              >
                Profit
              </span>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    color: "#22c55e",
                    fontWeight: "bold",
                    fontSize: containerHeight < 450 ? "16px" : "22px",
                  }}
                >
                  +${payout.toFixed(2)}
                </div>
                <div
                  style={{
                    color: "#9ca3af",
                    fontSize: containerHeight < 450 ? 11 : 16,
                    marginTop: "2px",
                  }}
                >
                  {percent}%
                </div>
              </div>
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }}></div>

          {/* Buttons - Switch to column for normal, maybe row for SUPER compact? Keep column but smaller */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              gap: containerHeight < 450 ? 5 : 10,
            }}
          >
            <button
              disabled={isBettingDisabled}
              onClick={() => handleBet("high")}
              style={{
                flex: 1,
                backgroundColor: isBettingDisabled ? "#555" : "#22c55e",
                color: "white",
                border: "none",
                borderRadius: 4,
                padding: containerHeight < 450 ? "8px" : "14px",
                cursor: isBettingDisabled ? "not-allowed" : "pointer",
                fontWeight: "bold",
                fontSize: containerHeight < 450 ? "13px" : "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: isBettingDisabled
                  ? "none"
                  : "0 4px 6px rgba(34, 197, 94, 0.2)",
                transition: "transfrom 0.1s",
                opacity: isBettingDisabled ? 0.7 : 1,
              }}
            >
              <span>CALL</span>
              <span style={{ fontSize: "12px", opacity: 0.8 }}>▲</span>
            </button>

            <button
              hidden={tab.typ === "forex"} // For Forex, maybe we want different buttons or same? User said "for both forex and crypto". HighChat hides them for Forex usually because Forex has separate buy/sell in sidebar?
              // Actually, user said "for both forex and crypto... disable btns".
              // If Split.js handles Forex via these buttons too (verified in handleBet logic), then we keep them.
              // But HighChat usually does Forex differently.
              // In Split.js, handleBet checks "tab?.typ === 'forex'". So these buttons WORK for Forex.
              // I will NOT hide them unless requested.
              disabled={isBettingDisabled}
              onClick={() => handleBet("low")}
              style={{
                flex: 1,
                backgroundColor: isBettingDisabled ? "#555" : "#ef4444",
                color: "white",
                border: "none",
                borderRadius: 4,
                padding: containerHeight < 450 ? "8px" : "14px",
                cursor: isBettingDisabled ? "not-allowed" : "pointer",
                fontWeight: "bold",
                fontSize: containerHeight < 450 ? "13px" : "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: isBettingDisabled
                  ? "none"
                  : "0 4px 6px rgba(239, 68, 68, 0.2)",
                transition: "transfrom 0.1s",
                opacity: isBettingDisabled ? 0.7 : 1,
              }}
            >
              <span>PUT</span>
              <span style={{ fontSize: "12px", opacity: 0.8 }}>▼</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flow5sCandles;
